import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  MapPin,
  Star,
  Scissors,
  Sparkles,
  ShieldCheck,
  Calendar,
  Clock,
  ArrowRight,
  Smile,
  Store,
  TrendingUp,
  CheckCircle2,
  Award,
  HeartHandshake,
} from 'lucide-react';
import { useVerifiedShops } from '../../api';
import { useServicesLocation } from '../../hooks/useServicesLocation';
import { formatShopAddress } from '../../utils/shopAddress';
import { homeGenderPath, parseHomeGender } from '../../utils/homeGender';
import styles from './home.module.css';

// Men's Lookbook images
import maleImg1 from '../../Images/barbers-7-400x480.jpg';
import maleImg2 from '../../Images/about-1-300x460.jpg';
import maleImg3 from '../../Images/about-3-300x460.jpg';
import maleImg4 from '../../Images/barbers-8-400x480.jpg';
import maleExperienceImg from '../../Images/home-1-585x580.jpg';

// Women's Lookbook images
import femaleImg1 from '../../Images/look-studio-FFX0xE9xCYg-unsplash.jpg';
import femaleImg2 from '../../Images/sonia-roselli-HEgs5fwWiuw-unsplash.jpg';
import femaleImg3 from '../../Images/young-woman-holding-makeup-brushes-sitting-studio.jpg';
import femaleImg4 from '../../Images/engin-akyurt-g-m8EDc4X6Q-unsplash.jpg';
import femaleExperienceImg from '../../Images/avdalyan-4MxD-wt9cas-unsplash.jpg';

// Testimonials avatars
import testAvatar1 from '../../Images/testimonials-1-100x100.jpg';
import testAvatar2 from '../../Images/testimonials-2-100x100.jpg';
import testAvatar3 from '../../Images/testimonials-3-100x100.jpg';
import testAvatar4 from '../../Images/testimonials-4-100x100.jpg';

// Hero background images
import maleHeroBg from '../../Images/luxury-barber-hero.jpg';
import femaleHeroBg from '../../Images/luxury-salon-hero.jpg';

// Fallback salon images
import fallbackSalon1 from '../../Images/barbers-1-370x310.jpg';
import fallbackSalon2 from '../../Images/barbers-2-370x310.jpg';
import fallbackSalon3 from '../../Images/barbers-4-370x310.jpg';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');

  const currentGender = parseHomeGender(location.search);
  const isFemale = currentGender === 'female';

  const { openLocationModal, selectedCityName, usedGeolocation, geoAttemptDone } =
    useServicesLocation();

  const locationLabel = !geoAttemptDone
    ? 'Location…'
    : selectedCityName
      ? selectedCityName
      : usedGeolocation
        ? 'Near Me'
        : 'Select City';

  // Fetch verified salons
  const { data: salons = [], isLoading: salonsLoading } = useVerifiedShops();

  // Top rated salons
  const topSalons = salons.slice(0, 6);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/salons?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/salons');
    }
  };

  const categories = isFemale
    ? [
        { name: 'Haircut & Styling', count: '45+ services', icon: Scissors, query: 'haircut' },
        { name: 'Hair Color & Highlights', count: '30+ services', icon: Sparkles, query: 'color' },
        { name: 'Facial & Cleanup', count: '28+ services', icon: Smile, query: 'facial' },
        { name: 'Bridal & Makeup', count: '18+ services', icon: Sparkles, query: 'bridal' },
        { name: 'Manicure & Pedicure', count: '22+ services', icon: Sparkles, query: 'pedicure' },
        { name: 'Spa & Body Massage', count: '15+ services', icon: HeartHandshake, query: 'spa' },
      ]
    : [
        { name: 'Classic Haircuts', count: '50+ salons', icon: Scissors, query: 'haircut' },
        { name: 'Beard Trim & Shape', count: '42+ salons', icon: Scissors, query: 'beard' },
        { name: 'Hair Spa & Scalp Care', count: '24+ salons', icon: Sparkles, query: 'spa' },
        { name: 'Facial & Detan', count: '32+ salons', icon: Smile, query: 'facial' },
        { name: 'Hair Color & Highlights', count: '19+ salons', icon: Sparkles, query: 'color' },
        { name: 'Groom Packages', count: '12+ salons', icon: ShieldCheck, query: 'groom' },
      ];

  // Dynamic Lookbook items based on gender
  const lookbookStyles = isFemale
    ? [
        {
          title: 'Balayage & Gloss Finish',
          desc: 'Seamless sun-kissed ombre color blend with shine-infusing gloss conditioning.',
          price: '₹1,499',
          tag: 'Trending',
          image: femaleImg1,
          query: 'balayage',
        },
        {
          title: 'Hydra-Facial Deep Detox',
          desc: 'Pore cleansing vacuum therapy with organic fruit enzyme brightening pack.',
          price: '₹999',
          tag: 'Most Booked',
          image: femaleImg2,
          query: 'facial',
        },
        {
          title: 'Bridal & Occasion Makeup',
          desc: 'High-definition airbrush look with full-day setting seal and eye lash sculpting.',
          price: '₹2,499',
          tag: 'Signature',
          image: femaleImg3,
          query: 'makeup',
        },
        {
          title: 'Keratin Smooth Treatment',
          desc: 'Deep nourishing keratin smoothing for frizz-free, silky straight styling.',
          price: '₹1,199',
          tag: 'Top Rated',
          image: femaleImg4,
          query: 'keratin',
        },
      ]
    : [
        {
          title: 'The Executive Fade & Beard',
          desc: 'Skin taper fade with precision shear work and hot towel razor contouring.',
          price: '₹399',
          tag: 'Top Rated',
          image: maleImg1,
          query: 'fade',
        },
        {
          title: 'Classic Scissor Crop',
          desc: 'Hand-crafted scissor texture cut finished with matte clay styling.',
          price: '₹349',
          tag: "Editor's Pick",
          image: maleImg2,
          query: 'haircut',
        },
        {
          title: 'Royal Shave & Detan Cleanse',
          desc: 'Straight razor hot foam shave followed by activated charcoal face detan.',
          price: '₹499',
          tag: 'Signature',
          image: maleImg3,
          query: 'shave',
        },
        {
          title: 'Beard Sculpt & Argan Serum',
          desc: 'Sharp geometry lineup with beard steam and organic argan oil nourishment.',
          price: '₹249',
          tag: 'Trending',
          image: maleImg4,
          query: 'beard',
        },
      ];

  // Dynamic Testimonials
  const testimonials = isFemale
    ? [
        {
          quote:
            'The hydra-facial and hair color were phenomenal. Transparent pricing with zero surprise chair charges was so refreshing!',
          name: 'Pooja Verma',
          meta: 'Visited Looks Salon · 5 Stars',
          avatar: testAvatar2,
        },
        {
          quote:
            'Booked my bridal trial makeup here. The stylist was so attentive and punctual. Highly recommend B4BARBER!',
          name: 'Sneha Patel',
          meta: 'Visited Glamour Studio · 5 Stars',
          avatar: testAvatar4,
        },
        {
          quote:
            'Such a breeze to book a manicure and hair blow-dry after work. No phone calls, just walk in and sit down.',
          name: 'Ananya Deshmukh',
          meta: 'Visited Enrich Spa · 5 Stars',
          avatar: testAvatar1,
        },
      ]
    : [
        {
          quote:
            'Found the best skin fade of my life in Noida. Booking took 30 seconds and the barber was ready the second I walked in.',
          name: 'Aarav Sharma',
          meta: 'Visited Truefitt Barbers · 5 Stars',
          avatar: testAvatar1,
        },
        {
          quote:
            'Never waiting in a crowded weekend salon queue again. Seamless appointment and razor-sharp beard lineup.',
          name: 'Rohan Malhotra',
          meta: 'Visited The Gentleman’s Club · 5 Stars',
          avatar: testAvatar3,
        },
        {
          quote:
            'Clean blades, single-use towels, and fixed pricing. This is how barbershop visits should always be.',
          name: 'Karan Mehra',
          meta: 'Visited Royal Scissors · 5 Stars',
          avatar: testAvatar4,
        },
      ];

  const heroBg = isFemale ? femaleHeroBg : maleHeroBg;

  return (
    <div className={styles.homeContainer}>
      {/* Hero Section */}
      <section
        className={styles.heroSection}
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.35)), url(${heroBg})`,
        }}
      >
        <div className={styles.heroContentWrapper}>
          <div className={styles.heroBadge}>
            <Sparkles size={13} />
            <span>India's Premier Barbershop &amp; Salon Network</span>
          </div>

          <h1 className={styles.heroTitle}>
            Book Top-Rated <span className={styles.heroHighlight}>Salons &amp; Stylists</span> Near
            You.
          </h1>

          <p className={styles.heroSubtitle}>
            Instant appointments, transparent pricing, and verified grooming professionals.
            Experience the finest haircuts, styling, and wellness treatments.
          </p>

          {/* Quick Search Bar */}
          <form className={styles.searchBarCard} onSubmit={handleSearch}>
            <button
              type="button"
              className={styles.searchLocationBtn}
              onClick={openLocationModal}
              title="Change your current city"
            >
              <MapPin size={15} style={{ color: '#dc2626' }} />
              <span>{locationLabel}</span>
            </button>

            <div className={styles.searchInputWrapper}>
              <Search size={16} style={{ color: '#94a3b8' }} />
              <input
                type="text"
                placeholder={
                  isFemale
                    ? 'Search hair styling, facials, bridal makeup, salon name…'
                    : 'Search haircuts, beard trims, scalp care, barbershops…'
                }
                className={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <button type="submit" className={styles.searchSubmitBtn}>
              <span>Explore Salons</span>
              <ArrowRight size={15} />
            </button>
          </form>

          {/* Quick Category Pills */}
          <div className={styles.quickPills}>
            <span style={{ fontSize: '0.775rem', color: '#94a3b8', marginRight: '4px' }}>
              Popular:
            </span>
            {(isFemale
              ? ['Hair Color', 'Hydra Facial', 'Bridal Makeup', 'Keratin', 'Pedicure']
              : ['Skin Fade', 'Beard Trim', 'Hair Spa', 'Detan Facial', 'Hot Towel Shave']
            ).map((term, idx) => (
              <Link
                key={idx}
                to={`/salons?search=${encodeURIComponent(term)}`}
                className={styles.quickPill}
              >
                {term}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className={styles.contentSection}>
        {/* Gender Toggle Row */}
        <div className={styles.genderToggleRow}>
          <div className={styles.genderSwitch}>
            <button
              type="button"
              className={`${styles.genderSwitchBtn} ${!isFemale ? styles.genderSwitchBtnActive : ''}`}
              onClick={() => navigate(homeGenderPath('male'))}
            >
              Men's Grooming
            </button>
            <button
              type="button"
              className={`${styles.genderSwitchBtn} ${isFemale ? styles.genderSwitchBtnActive : ''}`}
              onClick={() => navigate(homeGenderPath('female'))}
            >
              Women's Salon &amp; Spa
            </button>
          </div>
        </div>

        {/* Categories Section */}
        <div style={{ marginBottom: '3.5rem' }}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>
                Popular {isFemale ? 'Beauty & Salon' : 'Grooming'} Categories
              </h2>
              <p className={styles.sectionSubtitle}>
                Explore treatments by verified master barbers and stylists
              </p>
            </div>
            <Link to="/services" className={styles.viewAllLink}>
              <span>All Services</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className={styles.categoriesGrid}>
            {categories.map((cat, idx) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={idx}
                  to={`/salons?search=${encodeURIComponent(cat.query)}`}
                  className={styles.categoryCard}
                >
                  <div className={styles.categoryIconBox}>
                    <Icon size={24} />
                  </div>
                  <div className={styles.categoryName}>{cat.name}</div>
                  <div className={styles.categorySubtext}>{cat.count}</div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Visual Lookbook / Trending Styles Section */}
        <div className={styles.lookbookSection}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>
                {isFemale ? 'Signature Salon & Beauty Styles' : 'Trending Cuts & Beard Styles'}
              </h2>
              <p className={styles.sectionSubtitle}>
                {isFemale
                  ? 'Curated luxury treatments from premier hair & beauty specialists'
                  : 'Popular master barber crafts tailored for the modern gentleman'}
              </p>
            </div>
            <Link
              to={`/salons?search=${encodeURIComponent(isFemale ? 'hair' : 'fade')}`}
              className={styles.viewAllLink}
            >
              <span>Explore Looks</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className={styles.lookbookGrid}>
            {lookbookStyles.map((item, idx) => (
              <Link
                key={idx}
                to={`/salons?search=${encodeURIComponent(item.query)}`}
                className={styles.lookbookCard}
              >
                <div className={styles.lookbookMedia}>
                  <img src={item.image} alt={item.title} className={styles.lookbookImg} />
                  <div className={styles.lookbookOverlay} />
                  <span className={styles.lookbookTag}>{item.tag}</span>
                  <span className={styles.lookbookPrice}>{item.price}</span>
                </div>

                <div className={styles.lookbookBody}>
                  <h3 className={styles.lookbookTitle}>{item.title}</h3>
                  <p className={styles.lookbookDesc}>{item.desc}</p>
                  <div className={styles.lookbookBtn}>
                    <span>Find Salon Offering This</span>
                    <ArrowRight size={13} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Featured Salons Section */}
        <div style={{ marginBottom: '3.5rem' }}>
          <div className={styles.sectionHeader}>
            <div>
              <h2 className={styles.sectionTitle}>Featured Barbershops &amp; Salons</h2>
              <p className={styles.sectionSubtitle}>
                Verified partners known for exceptional hygiene, craft, and punctuality
              </p>
            </div>
            <Link to="/salons" className={styles.viewAllLink}>
              <span>View All Salons</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          {salonsLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <Scissors size={28} style={{ color: '#dc2626', marginBottom: '8px' }} />
              <p>Discovering verified salons in your city…</p>
            </div>
          ) : topSalons.length > 0 ? (
            <div className={styles.salonsGrid}>
              {topSalons.map((salon, idx) => {
                const fallbackImages = isFemale
                  ? [femaleImg1, femaleImg2, femaleImg3, femaleImg4, femaleExperienceImg]
                  : [
                      fallbackSalon1,
                      fallbackSalon2,
                      fallbackSalon3,
                      maleExperienceImg,
                      maleImg1,
                      maleImg2,
                    ];
                const coverImage =
                  salon.images?.[0]?.url ||
                  salon.images?.[0] ||
                  fallbackImages[idx % fallbackImages.length];
                const addr = formatShopAddress(salon) || 'Salon Location';
                const rating = salon.rating ? Number(salon.rating).toFixed(1) : '4.8';
                const servicesCount = salon.products?.length || 0;

                return (
                  <Link key={salon._id} to={`/salon/${salon._id}`} className={styles.salonCard}>
                    <div className={styles.salonImageWrap}>
                      <img src={coverImage} alt={salon.name} className={styles.salonImage} />

                      <span className={styles.salonRatingPill}>
                        <Star size={11} fill="#fbbf24" stroke="#fbbf24" />
                        <span>{rating}</span>
                      </span>

                      {salon.verified && <span className={styles.salonVerifiedTag}>Verified</span>}
                    </div>

                    <div className={styles.salonBody}>
                      <h3 className={styles.salonName} title={salon.name}>
                        {salon.name}
                      </h3>

                      <div className={styles.salonAddress} title={addr}>
                        <MapPin
                          size={12}
                          style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }}
                        />
                        <span>{addr}</span>
                      </div>

                      <div className={styles.salonFooter}>
                        <span className={styles.salonServicesCount}>{servicesCount} Services</span>
                        <div className={styles.btnBookSalon}>
                          <span>Book Slot</span>
                          <ArrowRight size={12} />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '3rem',
                background: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
              }}
            >
              <Store size={36} style={{ color: '#94a3b8', marginBottom: '8px' }} />
              <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 4px 0' }}>
                No salons found in this location
              </h3>
              <p style={{ fontSize: '0.825rem', color: '#64748b', margin: '0 0 1rem 0' }}>
                Try switching cities or selecting "All Areas" to browse nationwide partners.
              </p>
              <button type="button" className={styles.searchSubmitBtn} onClick={openLocationModal}>
                Change City
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Salon Experience Showcase Section */}
      <section className={styles.experienceSection}>
        <div className={styles.experienceContainer}>
          <div className={styles.experienceMediaWrap}>
            <img
              src={isFemale ? femaleExperienceImg : maleExperienceImg}
              alt={isFemale ? 'Luxury Salon Styling' : 'Barbershop Craft'}
              className={styles.experienceMediaImg}
            />
            <div className={styles.experienceFloatingBadge}>
              <div className={styles.floatingBadgeIcon}>
                <Award size={20} />
              </div>
              <div className={styles.floatingBadgeText}>
                <span className={styles.floatingBadgeBold}>100% Certified Salons</span>
                Physical hygiene &amp; master craftsmanship audits
              </div>
            </div>
          </div>

          <div className={styles.experienceContent}>
            <span className={styles.experienceTag}>The B4BARBER Standard</span>
            <h2 className={styles.experienceTitle}>
              {isFemale
                ? 'Elevating Beauty & Haircare Into A Serene Ritual'
                : 'Elevating Everyday Grooming Into An Experience'}
            </h2>
            <p className={styles.experienceSubtitle}>
              {isFemale
                ? 'Relax knowing you will experience sanitized equipment, gentle certified stylists, and zero queue delays at every partner venue.'
                : 'Step into any partner salon knowing you are guaranteed punctuality, clean stations, and seasoned master barbers.'}
            </p>

            <div className={styles.experienceList}>
              <div className={styles.experienceItem}>
                <div className={styles.experienceItemIcon}>
                  <Clock size={18} />
                </div>
                <div className={styles.experienceItemContent}>
                  <h4>Zero Chair Waiting Time</h4>
                  <p>
                    Your time slot is strictly held. Walk in at your scheduled time and take the
                    chair immediately.
                  </p>
                </div>
              </div>

              <div className={styles.experienceItem}>
                <div className={styles.experienceItemIcon}>
                  <CheckCircle2 size={18} />
                </div>
                <div className={styles.experienceItemContent}>
                  <h4>Autoclaved &amp; Sanitized Tools</h4>
                  <p>
                    100% single-use razors, sanitized scissors, and clean capes for every customer.
                  </p>
                </div>
              </div>

              <div className={styles.experienceItem}>
                <div className={styles.experienceItemIcon}>
                  <Sparkles size={18} />
                </div>
                <div className={styles.experienceItemContent}>
                  <h4>Consultation First</h4>
                  <p>
                    Every service starts with a personal consultation to match cuts to your face
                    shape and personal style.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Link to="/salons" className={styles.searchSubmitBtn}>
                <span>Book Your Experience</span>
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews & Transformations */}
      <section className={styles.testimonialsSection}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>What Our Clients Say</h2>
            <p className={styles.sectionSubtitle}>
              Real experiences from verified appointments across India
            </p>
          </div>
        </div>

        <div className={styles.testimonialsGrid}>
          {testimonials.map((t, idx) => (
            <div key={idx} className={styles.testimonialCard}>
              <div className={styles.testimonialStars}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#fbbf24" stroke="#fbbf24" />
                ))}
              </div>
              <p className={styles.testimonialQuote}>"{t.quote}"</p>
              <div className={styles.testimonialAuthor}>
                <img src={t.avatar} alt={t.name} className={styles.testimonialAvatar} />
                <div className={styles.testimonialAuthorInfo}>
                  <span className={styles.testimonialName}>{t.name}</span>
                  <span className={styles.testimonialMeta}>{t.meta}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Value Proposition Strip */}
      <section className={styles.valueSection}>
        <div className={styles.valueGrid}>
          <div className={styles.valueCard}>
            <div className={styles.valueIconBox}>
              <ShieldCheck size={22} />
            </div>
            <div className={styles.valueContent}>
              <div className={styles.valueTitle}>100% Verified Stylists</div>
              <p className={styles.valueDesc}>
                Every salon is physically inspected for hygiene and professional quality standards.
              </p>
            </div>
          </div>

          <div className={styles.valueCard}>
            <div className={styles.valueIconBox}>
              <Calendar size={22} />
            </div>
            <div className={styles.valueContent}>
              <div className={styles.valueTitle}>Instant Slot Confirmation</div>
              <p className={styles.valueDesc}>
                Real-time appointment scheduling with zero waiting time at the barbershop chair.
              </p>
            </div>
          </div>

          <div className={styles.valueCard}>
            <div className={styles.valueIconBox}>
              <TrendingUp size={22} />
            </div>
            <div className={styles.valueContent}>
              <div className={styles.valueTitle}>Transparent Pricing</div>
              <p className={styles.valueDesc}>
                No hidden costs or chair surcharges. Pay exact prices listed on the service menu.
              </p>
            </div>
          </div>

          <div className={styles.valueCard}>
            <div className={styles.valueIconBox}>
              <Clock size={22} />
            </div>
            <div className={styles.valueContent}>
              <div className={styles.valueTitle}>Flexible Rescheduling</div>
              <p className={styles.valueDesc}>
                Change or cancel your appointments easily through your user account dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Barbershop Partner Invitation Banner */}
      <div className={styles.partnerBannerSection} style={{ marginTop: '3.5rem' }}>
        <div className={styles.partnerBanner}>
          <div className={styles.partnerBannerContent}>
            <h3 className={styles.partnerBannerTitle}>Are you a Barbershop or Salon Owner?</h3>
            <p className={styles.partnerBannerText}>
              Join the B4BARBER Partner Network to accept digital appointments, automate slot
              management, and receive direct daily bank settlements.
            </p>
          </div>
          <a
            href="http://localhost:3004/login"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.partnerBannerBtn}
          >
            <span>Partner Console</span>
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
